import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

import styles from "./integrations.module.css";

type BannerTone = "info" | "warning" | "danger" | "good" | "neutral";

const BANNER: Record<BannerTone, string> = {
  info: styles.bannerInfo,
  warning: styles.bannerWarning,
  danger: styles.bannerDanger,
  good: styles.bannerGood,
  neutral: "",
};

/** A line across the page that says something about all of it, with what to do. */
export function Banner({
  tone = "neutral",
  title,
  children,
  actions,
  icon,
}: {
  tone?: BannerTone;
  title?: string;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={`${styles.banner} ${BANNER[tone]}`} role={tone === "danger" || tone === "warning" ? "alert" : "status"}>
      {icon}
      <div className={styles.bannerText}>
        {title && <span className={styles.bannerTitle}>{title} </span>}
        {children}
      </div>
      {actions && <div className={styles.bannerActions}>{actions}</div>}
    </div>
  );
}

/** Rows in the shape of the content, while it loads. */
export function SkeletonRows({ rows = 4, columns = 4, label = "Loading" }: { rows?: number; columns?: number; label?: string }) {
  return (
    <div className={styles.skeleton} aria-busy="true" aria-label={label} role="status">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={styles.skeletonRow} style={{ "--cols": columns } as CSSProperties}>
          {Array.from({ length: columns }, (_, column) => (
            <span key={column} className={styles.skeletonBlock} style={{ width: `${60 + ((row + column) % 4) * 10}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export type TabItem = { key: string; label: string; href: string; count?: number; warn?: boolean };

/** Tabs that are links, so each one has its own address. */
export function Tabs({ items, current, label }: { items: TabItem[]; current: string; label: string }) {
  return (
    <nav className={styles.tabs} aria-label={label}>
      {items.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          className={item.key === current ? `${styles.tab} ${styles.tabOn}` : styles.tab}
          aria-current={item.key === current ? "page" : undefined}
        >
          {item.label}
          {item.count !== undefined && (
            <span className={item.warn && item.count > 0 ? `${styles.tabCount} ${styles.tabCountWarn}` : styles.tabCount}>
              {item.count}
            </span>
          )}
        </Link>
      ))}
    </nav>
  );
}
